/**
 * LIBRARY: Supabase Filters
 * Helper pour traduire les FilterCondition en requêtes Supabase PostgREST
 */

import type { PostgrestFilterBuilder } from '@supabase/postgrest-js';
import type { FilterCondition, FilterMode } from '../types/filters';
import { escapeOrSearchTerm, escapeLikeMethod, alwaysFalseCondition } from './postgrestSearch';

/**
 * Formate une valeur destinée à être interpolée dans une CHAÎNE `.or()` / `and()`
 * PostgREST (opérateurs d'égalité / comparaison). Si elle contient un délimiteur
 * d'arbre logique (`,` `(` `)`) ou un guillemet/backslash, on la double-quote —
 * PostgREST autorise les valeurs entre guillemets pour contenir ces caractères —
 * en échappant `\` puis `"`. Sinon elle est laissée brute (zéro changement sur les
 * dates / ids / nombres / booléens : aucune régression). Préserve l'égalité EXACTE
 * (contrairement à un strip). Vérifié e2e : `name.eq."a,b"` matche bien la ligne
 * "a,b" et ne casse pas le parse (revue 2026-06-01).
 *
 * Ne s'applique PAS aux opérateurs ILIKE (qui passent par `escapeOrSearchTerm`),
 * ni à la forme MÉTHODE `.ilike()` / `.eq()` (déjà paramétrée par supabase-js).
 */
function formatOrValue(value: string | boolean | null): string | boolean | null {
  if (typeof value !== 'string') return value;
  if (/[,()"\\]/.test(value)) {
    return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  }
  return value;
}

/**
 * Retour réunion 07/07 (C) : un filtre « date créée est égal à = 11 juin »
 * renvoyait 0 ligne car `equals` produisait `created_at.eq.2026-06-11`
 * (égalité EXACTE de timestamp) alors que la colonne porte une heure. L'input
 * `<input type="date">` émet une date NUE `YYYY-MM-DD` (sans heure) : on la
 * détecte pour transformer `equals`/`notEquals` sur date nue en range de jour
 * inclusif [00:00:00 → 23:59:59.999], comme l'opérateur `sameDay`. Path-agnostique
 * (barre de filtres ET filtre colonne liste). Faux positif quasi nul : une valeur
 * texte `equals` valant exactement `YYYY-MM-DD` est extrêmement improbable dans ce
 * CRM (noms/emails/n°) et ne sert jamais de comparaison temporelle.
 */
const BARE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function isBareDate(value: unknown): value is string {
  return typeof value === 'string' && BARE_DATE_RE.test(value);
}
function dayStart(day: string): string {
  return `${day}T00:00:00`;
}
function dayEnd(day: string): string {
  return `${day}T23:59:59.999`;
}

/**
 * Convertit un filtre en syntaxe PostgREST
 */
function filterToPostgrestSyntax(filter: FilterCondition): string {
  const { field, operator, value } = filter;

  // Conversion des booléens string
  let filterValue: string | boolean | null = Array.isArray(value) ? value[0] : value;
  if (filterValue === 'true') filterValue = true as unknown as string;
  if (filterValue === 'false') filterValue = false as unknown as string;

  // Valeur échappée pour les motifs ILIKE : on neutralise les wildcards (% _ *)
  // et on retire les caractères de syntaxe .or() (, ( )) du CONTENU saisi. Les
  // `*` qui entourent la valeur dans les templates ci-dessous restent les jokers
  // voulus. Les opérateurs d'égalité/comparaison passent eux par formatOrValue
  // (double-quote) — revue 2026-06-01.
  const likeValue = typeof filterValue === 'string' ? escapeOrSearchTerm(filterValue) : filterValue;

  // Garde finding-3/B : un motif ILIKE dont le contenu se réduit à des caractères
  // de syntaxe .or() (`, ( )`) devient vide après nettoyage. Sur un opérateur
  // POSITIF (contains/startsWith/endsWith), émettre `*..*` matcherait tout, et le
  // simple skip ('') laisserait — s'il est le seul filtre actif — la query sans
  // contrainte (= match-all). On émet donc une condition TOUJOURS FAUSSE. Sur un
  // opérateur NÉGATIF (notContains/...), "exclure ce qui ne matche (quasi) rien"
  // ≈ aucune contrainte : on skippe ('').
  // NB angle mort assumé : le chemin méthode (mode AND non imbriqué) ne strippe
  // PAS `, ( )` (escapeLikeMethod) et exclut donc correctement en négatif ; seul
  // ce chemin chaîne perd l'exclusion négative sur un terme `,()`-only (Low).
  const isPositiveIlike = ['contains', 'startsWith', 'endsWith'].includes(operator);
  const isNegativeIlike = ['notContains', 'notStartsWith', 'notEndsWith'].includes(operator);
  if ((isPositiveIlike || isNegativeIlike) && likeValue === '') {
    return isPositiveIlike ? alwaysFalseCondition(field) : '';
  }

  switch (operator) {
    case 'equals':
      if (filterValue === null || filterValue === 'null') {
        return `${field}.is.null`;
      }
      // C : date nue « = jour » → range de jour inclusif (cf. isBareDate).
      if (isBareDate(filterValue)) {
        return `and(${field}.gte.${dayStart(filterValue)},${field}.lte.${dayEnd(filterValue)})`;
      }
      return `${field}.eq.${formatOrValue(filterValue)}`;

    case 'notEquals':
      if (filterValue === null || filterValue === 'null') {
        return `${field}.not.is.null`;
      }
      // C : « ≠ jour » sur date nue → hors du jour entier (avant OU après).
      if (isBareDate(filterValue)) {
        return `or(${field}.lt.${dayStart(filterValue)},${field}.gt.${dayEnd(filterValue)})`;
      }
      return `${field}.neq.${formatOrValue(filterValue)}`;

    case 'notEqualsOrNull':
      // "field <> value OU field IS NULL" : variante null-safe de notEquals.
      // L'unique usage passe la constante 'expired' (masquage BPI expirés, DATA-02),
      // mais on double-quote quand même via formatOrValue : si un 2e appelant arrive
      // avec du texte contenant `,()`, l'arbre logique .or() ne casse pas (revue
      // 2026-06-01).
      if (filterValue === null || filterValue === 'null') {
        // "différent de NULL OU NULL" = aucune contrainte.
        return '';
      }
      return `or(${field}.is.null,${field}.neq.${formatOrValue(filterValue)})`;

    case 'contains':
      return `${field}.ilike.*${likeValue}*`;

    case 'notContains':
      return `not.${field}.ilike.*${likeValue}*`;

    case 'startsWith':
      return `${field}.ilike.${likeValue}*`;

    case 'notStartsWith':
      return `not.${field}.ilike.${likeValue}*`;

    case 'endsWith':
      return `${field}.ilike.*${likeValue}`;

    case 'notEndsWith':
      return `not.${field}.ilike.*${likeValue}`;

    case 'greaterThan':
      return `${field}.gt.${formatOrValue(filterValue)}`;

    case 'lessThan':
      return `${field}.lt.${formatOrValue(filterValue)}`;

    case 'before':
      return `${field}.lt.${formatOrValue(filterValue)}`;

    case 'after':
      return `${field}.gt.${formatOrValue(filterValue)}`;

    case 'between':
      if (Array.isArray(value) && value.length === 2) {
        // Pour between, on doit générer deux conditions
        return `and(${field}.gte.${formatOrValue(value[0])},${field}.lte.${formatOrValue(value[1])})`;
      }
      return '';

    case 'sameDay': {
      // Génère un range [start_of_day, end_of_day] (UTC) à partir d'une date YYYY-MM-DD
      const dayStr = typeof filterValue === 'string' ? filterValue : '';
      if (!dayStr) return '';
      const start = `${dayStr}T00:00:00`;
      const end = `${dayStr}T23:59:59.999`;
      return `and(${field}.gte.${start},${field}.lte.${end})`;
    }

    case 'isNotNull':
      return `${field}.not.is.null`;

    case 'isNull':
      return `${field}.is.null`;

    default:
      console.warn(`[filterToPostgrestSyntax] Opérateur non supporté: ${operator}`);
      return '';
  }
}

/**
 * Applique des filtres en mode AND à une query
 */
function applyAndFilters<T extends Record<string, any>>(
  query: PostgrestFilterBuilder<any, T, any>,
  filters: FilterCondition[]
): PostgrestFilterBuilder<any, T, any> {
  let result = query;

  for (const filter of filters) {
    const { field, operator, value } = filter;

    // Conversion des booléens string en boolean
    let filterValue: string | string[] | boolean | null = value;
    if (value === 'true') filterValue = true;
    if (value === 'false') filterValue = false;

    // Détecter les champs imbriqués (contiennent un point)
    const isNestedField = field.includes('.');

    if (isNestedField) {
      // Syntaxe PostgREST pour filtrer sur des champs de relations
      const postgrestFilter = filterToPostgrestSyntax(filter);
      if (postgrestFilter) {
        result = result.or(postgrestFilter);
      }
    } else {
      // Champs normaux : utiliser les méthodes standard de supabase-js.
      // Pour les motifs ILIKE on échappe `\ % _` (PAS `*`, littéral en LIKE) afin que
      // le mode AND (forme méthode) traite les wildcards à l'identique du mode OR
      // (forme chaîne, via escapeOrSearchTerm) — finding-2, revue 2026-06-01.
      const likeMethodValue =
        typeof filterValue === 'string' ? escapeLikeMethod(filterValue) : filterValue;
      switch (operator) {
        case 'equals':
          if (filterValue === null || filterValue === 'null') {
            result = result.is(field, null);
          } else if (isBareDate(filterValue)) {
            // C : date nue « = jour » → range de jour inclusif.
            result = result.gte(field, dayStart(filterValue)).lte(field, dayEnd(filterValue));
          } else {
            result = result.eq(field, filterValue);
          }
          break;

        case 'notEquals':
          if (filterValue === null || filterValue === 'null') {
            result = result.not(field, 'is', null);
          } else if (isBareDate(filterValue)) {
            // C : « ≠ jour » sur date nue → hors du jour entier (avant OU après).
            result = result.or(
              `${field}.lt.${dayStart(filterValue)},${field}.gt.${dayEnd(filterValue)}`
            );
          } else {
            result = result.neq(field, filterValue);
          }
          break;

        case 'notEqualsOrNull':
          // (field IS NULL OR field <> value) : null-safe. Un .neq seul écarterait
          // les lignes NULL (NULL <> 'x' = NULL). Le .or() est ANDé avec les autres
          // conditions de la query par PostgREST (cf. DATA-02 : masquer 'expired'
          // sans masquer les prospects sans BPI dont payment_status est NULL).
          if (filterValue === null || filterValue === 'null') {
            break; // "différent de NULL OU NULL" = aucune contrainte
          }
          // .or() = forme chaîne → double-quote la valeur (cohérent avec
          // filterToPostgrestSyntax) pour ne pas casser l'arbre logique si un futur
          // appelant passe du texte avec `,()`.
          result = result.or(
            `${field}.is.null,${field}.neq.${
              typeof filterValue === 'string' ? formatOrValue(filterValue) : filterValue
            }`
          );
          break;

        case 'contains':
          result = result.ilike(field, `%${likeMethodValue}%`);
          break;

        case 'notContains':
          result = result.not(field, 'ilike', `%${likeMethodValue}%`);
          break;

        case 'startsWith':
          result = result.ilike(field, `${likeMethodValue}%`);
          break;

        case 'notStartsWith':
          result = result.not(field, 'ilike', `${likeMethodValue}%`);
          break;

        case 'endsWith':
          result = result.ilike(field, `%${likeMethodValue}`);
          break;

        case 'notEndsWith':
          result = result.not(field, 'ilike', `%${likeMethodValue}`);
          break;

        case 'greaterThan':
          result = result.gt(field, filterValue);
          break;

        case 'lessThan':
          result = result.lt(field, filterValue);
          break;

        case 'before':
          result = result.lt(field, filterValue);
          break;

        case 'after':
          result = result.gt(field, filterValue);
          break;

        case 'between':
          if (Array.isArray(filterValue) && filterValue.length === 2) {
            result = result.gte(field, filterValue[0]).lte(field, filterValue[1]);
          }
          break;

        case 'sameDay': {
          // Filtre "le jour du" : génère un range [00:00:00, 23:59:59.999] sur la date donnée
          const dayStr = typeof filterValue === 'string' ? filterValue : '';
          if (dayStr) {
            result = result.gte(field, `${dayStr}T00:00:00`).lte(field, `${dayStr}T23:59:59.999`);
          }
          break;
        }

        case 'isNotNull':
          result = result.not(field, 'is', null);
          break;

        case 'isNull':
          result = result.is(field, null);
          break;

        default:
          console.warn(`[applyAndFilters] Opérateur non supporté: ${operator}`);
      }
    }
  }

  return result;
}

/**
 * Applique une liste de filtres à une query Supabase
 * Traduit les FilterCondition en méthodes PostgREST appropriées
 *
 * @param query - Query builder Supabase
 * @param filters - Liste des conditions de filtre à appliquer
 * @param filterMode - Mode de combinaison des filtres ('and' ou 'or')
 * @param requiredFilters - Filtres obligatoires toujours appliqués en mode AND (ex: filtre d'onglet)
 * @returns Query builder avec les filtres appliqués
 */
export function applyFiltersToQuery<T extends Record<string, any>>(
  query: PostgrestFilterBuilder<any, T, any>,
  filters: FilterCondition[],
  filterMode: FilterMode = 'and',
  requiredFilters: FilterCondition[] = []
): PostgrestFilterBuilder<any, T, any> {
  let result = query;

  // 1. D'abord appliquer les filtres obligatoires (toujours en mode AND)
  // Ex: filtre d'onglet de statut - ils s'appliquent toujours
  if (requiredFilters.length > 0) {
    result = applyAndFilters(result, requiredFilters);
  }

  // 2. Ensuite appliquer les filtres avancés avec leur mode
  if (filters.length === 0) {
    return result;
  }

  // Mode OR : utiliser .or() avec toutes les conditions combinées
  if (filterMode === 'or') {
    const orConditions = filters
      .map((filter) => filterToPostgrestSyntax(filter))
      .filter((condition) => condition !== '')
      .join(',');

    if (orConditions) {
      return result.or(orConditions);
    }
    return result;
  }

  // Mode AND : appliquer chaque filtre individuellement
  return applyAndFilters(result, filters);
}
